// pms-adapters / supabase / cancellationPolicy — the établissement's cancellation terms (PB7a, migration 20261026120100).
//
// DIRECT writes under RLS, as for the extras catalogue (D4): the scale's shape, the percentages, « only an OTA policy
// is verified » and the server-side stamp are the table's CHECKs and trigger, so the database refuses a bad policy
// whatever writes it. `verified_at` is written only by `verifyPolicy`, and only as « now »: the trigger replaces the
// value with the server's clock, so the client's clock never dates a check of the extranet.

import {
  err,
  ok,
  type CancellationPolicy,
  type CancellationPolicyRepository,
  type CancellationTier,
  type PrepaymentAt,
  type Result,
  type ScopeSource,
  type StoredCancellationPolicy,
} from '@lemontaigu/pms-core';
import { fromList, fromResult, fromVoid, scopedInsert, scopedQuery, type SupabaseClient } from './support.js';

export interface CancellationPolicyRow {
  id: number;
  code: string;
  label_fr: string;
  ota: boolean;
  prepayment_pct: number;
  prepayment_at: string;
  tiers: CancellationTier[];
  no_show_pct: number;
  verified_at: string | null;
}

const COLUMNS = 'id, code, label_fr, ota, prepayment_pct, prepayment_at, tiers, no_show_pct, verified_at';

export const rowToCancellationPolicy = (r: CancellationPolicyRow): StoredCancellationPolicy => ({
  id: r.id,
  code: r.code,
  labelFr: r.label_fr,
  ota: r.ota,
  prepaymentPct: r.prepayment_pct,
  prepaymentAt: r.prepayment_at as PrepaymentAt,
  tiers: r.tiers.map((t) => ({ moreThanDaysBefore: t.moreThanDaysBefore ?? null, retainPct: t.retainPct })),
  noShowPct: r.no_show_pct,
  verifiedAt: r.verified_at,
});

export class SupabaseCancellationPolicyRepository implements CancellationPolicyRepository {
  constructor(
    private readonly db: SupabaseClient,
    private readonly scope: ScopeSource,
  ) {}

  async listPolicies(): Promise<Result<StoredCancellationPolicy[]>> {
    return fromList<CancellationPolicyRow, StoredCancellationPolicy>(
      await scopedQuery(this.db.from('pms_cancellation_policy').select(COLUMNS), this.scope.current()).order('id'),
      rowToCancellationPolicy,
    );
  }

  async savePolicy(draft: Omit<CancellationPolicy, 'verifiedAt'> & { id?: number }): Promise<Result<number>> {
    const row = {
      code: draft.code,
      label_fr: draft.labelFr.trim(),
      ota: draft.ota,
      prepayment_pct: draft.prepaymentPct,
      prepayment_at: draft.prepaymentAt,
      tiers: draft.tiers.map((t) => ({ moreThanDaysBefore: t.moreThanDaysBefore, retainPct: t.retainPct })),
      no_show_pct: draft.noShowPct,
    };
    if (draft.id === undefined) {
      // A policy is one établissement's: never created without knowing which.
      const scoped = scopedInsert(this.scope.current());
      if (scoped['property_code'] === undefined) return err('validation', 'Choisissez d’abord un établissement.');
      const r = await this.db.from('pms_cancellation_policy').insert({ ...scoped, ...row }).select('id').single();
      if (r.error) return fromResult<number>(r);
      return ok((r.data as { id: number }).id);
    }
    const r = await scopedQuery(
      this.db.from('pms_cancellation_policy').update(row).eq('id', draft.id),
      this.scope.current(),
    )
      .select('id')
      .single();
    if (r.error) return fromResult<number>(r);
    return ok((r.data as { id: number }).id);
  }

  async deletePolicy(id: number): Promise<Result<void>> {
    return fromVoid(
      await scopedQuery(this.db.from('pms_cancellation_policy').delete().eq('id', id), this.scope.current()),
    );
  }

  async verifyPolicy(id: number): Promise<Result<void>> {
    return fromVoid(
      await scopedQuery(
        this.db.from('pms_cancellation_policy').update({ verified_at: new Date().toISOString() }).eq('id', id),
        this.scope.current(),
      ),
    );
  }

  async assignPlanPolicy(ratePlanId: number, policyId: number | null): Promise<Result<void>> {
    return fromVoid(
      await scopedQuery(
        this.db.from('pms_rate_plan').update({ cancellation_policy_id: policyId }).eq('id', ratePlanId),
        this.scope.current(),
      ),
    );
  }
}
